import React, { useState, useEffect, useMemo } from "react";
import Modal from "../../components/Modal";
import { chartOfAccounts } from "../../data/chartOfAccounts";
import { createTransaction } from "../../services/apiClient";
import { getCurrentUser } from "../../auth";
import { addPayment as addPaymentTx, deletePayment as deletePaymentTx, getPayments as fetchPaymentsSvc } from "../../services/transactionsService";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";
import { db } from "../../services/firebase";
import { collection, getDocs, addDoc, query, where } from "firebase/firestore";
import { getAllStudents as loadStudents, refreshStudentsFromFirestore } from "../../services/studentsService";

const initialPayments = [
  { id: 1, payer: "Juan Dela Cruz", amount: 5000, date: "2025-06-30", method: "Cash", reference: "OR-001" },
  { id: 2, payer: "Maria Santos", amount: 3500, date: "2025-06-29", method: "Bank Transfer", reference: "BT-123" }
];

export default function Payments() {
  const normCampus = (v) => {
    const x = String(v || "").toLowerCase().trim();
    if (x.includes("gapan")) return "gapan";
    if (x.includes("bulacan")) return "bulacan";
    return x;
  };
  const [payments, setPayments] = useState(() => {
    if (USE_LOCAL_FALLBACK) {
      try { return JSON.parse(localStorage.getItem("payments")) || initialPayments; } catch { return initialPayments; }
    }
    try { return JSON.parse(localStorage.getItem("payments_fs_cache")) || []; } catch { return []; }
  });
  const [showForm, setShowForm] = useState(false);
  const [campusFilter, setCampusFilter] = useState("");
  const [form, setForm] = useState({
    payer: "",
    amount: "",
    date: "",
    method: "Cash",
    reference: "",
    studentId: "",
    accountId: "",
    category: "Tuition",
    description: ""
  });

  // Load students (demo hidden in prod); refresh from Firestore in prod so campus fallback works
  const [studentsState, setStudentsState] = useState(() => loadStudents());
  useEffect(() => {
    if (!USE_LOCAL_FALLBACK) {
      refreshStudentsFromFirestore().then(() => {
        setStudentsState(loadStudents());
      }).catch(() => {
        setStudentsState(loadStudents());
      });
    }
  }, []);
  const allStudents = useMemo(() => studentsState, [studentsState]);

  const accounts = (() => {
    try {
      const raw = localStorage.getItem("accounts");
      return raw ? JSON.parse(raw) : chartOfAccounts;
    } catch {
      return chartOfAccounts;
    }
  })();
  const incomeAccounts = accounts.filter(a => a.type === "Income");

  const selectedStudent = (() => {
    const sid = form.studentId;
    return allStudents.find(s => s.id === sid) || null;
  })();
  const filteredIncomeAccounts = (() => {
    if (!selectedStudent) return incomeAccounts;
    return incomeAccounts.filter(a => !a.campus || a.campus === selectedStudent.campus);
  })();

  const currentUser = getCurrentUser();
  const userCampus = String(currentUser?.campus || "").toLowerCase();
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin" || userCampus === "all";
  const visiblePayments = useMemo(() => {
    const base = isAdmin ? payments : payments.filter(p => {
      const pc = normCampus(p.campus);
      if (pc) return pc === normCampus(userCampus);
      const st = allStudents.find(s => s.id === p.studentId);
      const sc = normCampus(st?.campus || "");
      return sc === normCampus(userCampus);
    });
    if (isAdmin && campusFilter) {
      const target = normCampus(campusFilter);
      return base.filter(p => {
        const pc = normCampus(p.campus);
        if (pc) return pc === target;
        const st = allStudents.find(s => s.id === p.studentId);
        const sc = normCampus(st?.campus || "");
        return sc === target;
      });
    }
    return base;
  }, [isAdmin, payments, allStudents, userCampus, campusFilter]);

  // Load from Firestore when not using local fallback
  useEffect(() => {
    if (USE_LOCAL_FALLBACK) return;
    // Clear legacy local-only cache to prevent "ghost" rows reappearing on reload
    try { localStorage.removeItem("payments"); } catch (err) { /* no-op */ }
    async function fetchPayments() {
      try {
        // Load all payments, normalize campus for consistent filtering
        const snap = await getDocs(collection(db, "payments"));
        const rows = snap.docs.map(d => {
          const data = d.data();
          return { id: d.id, ...data, campus: normCampus(data.campus) };
        });
        setPayments(rows);
        try { localStorage.setItem("payments_fs_cache", JSON.stringify(rows)); } catch (err) { /* no-op */ }
      } catch (e) {
        // No-op: keep local
      }
    }
    fetchPayments();
  }, [isAdmin, userCampus]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const student = selectedStudent;
    const campusCode = student ? String(student.campus || "").toLowerCase() || "gen" : "gen";
    const seriesStore = JSON.parse(localStorage.getItem("or_series_next") || "{}");
    let nextOR = Number(seriesStore[campusCode] || 0);
    if (!nextOR) {
      try {
        const localPays = JSON.parse(localStorage.getItem("payments") || "[]");
        const campusPays = localPays.filter(p => (p.campus || "").toLowerCase() === campusCode);
        const nums = campusPays.map(p => {
          const m = String(p.reference || "").match(/\d+/);
          return m ? Number(m[0]) : 0;
        }).filter(n => n > 0);
        nextOR = nums.length ? Math.max(...nums) + 1 : 1;
      } catch {
        nextOR = 1;
      }
    }
    const orRef = form.reference && form.reference.trim() ? form.reference.trim() : String(nextOR);
    const selectedAccount = form.accountId ? accounts.find(a => a.id === Number(form.accountId)) : null;
    const newPayment = {
      id: payments.length ? Math.max(...payments.map(p => p.id || 0)) + 1 : 1,
      payer: form.payer || (student ? (student.fullname || student.name) : ""),
      amount: Number(form.amount),
      date: form.date,
      method: form.method,
      reference: orRef,
      studentId: form.studentId,
      accountId: form.accountId ? Number(form.accountId) : null,
      campus: student ? String(student.campus || "").toLowerCase() : (userCampus || undefined),
      category: form.category || (selectedAccount ? selectedAccount.name : "Other"),
      description: form.description || ""
    };

    if (USE_LOCAL_FALLBACK) {
      const updatedPayments = addPaymentTx(newPayment);
      setPayments(updatedPayments);
    } else {
      try {
        await addDoc(collection(db, "payments"), newPayment);
        const snap = await getDocs(collection(db, "payments"));
        setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        // fallback to local if needed
        const updatedPayments = addPaymentTx(newPayment);
        setPayments(updatedPayments);
      }
    }

    // Update local OR series cache regardless
    seriesStore[campusCode] = Number(orRef.replace(/\D/g, "")) + 1;
    localStorage.setItem("or_series_next", JSON.stringify(seriesStore));

    setShowForm(false);
    setForm({ payer: "", amount: "", date: "", method: "Cash", reference: "", studentId: "", accountId: "", category: "Tuition", description: "" });
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <h2 className="mb-0">Payments</h2>
          {isAdmin && (
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0">Campus</label>
              <select
                className="form-select"
                value={campusFilter}
                onChange={e => setCampusFilter(e.target.value)}
                style={{ width: 160 }}
              >
                <option value="">All</option>
                <option value="gapan">gapan</option>
                <option value="bulacan">bulacan</option>
              </select>
            </div>
          )}
        </div>
        <button className="btn btn-warning" onClick={() => setShowForm(true)}>Record Payment</button>
      </div>
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Payment ID</th>
            <th>Payer</th>
            <th>Campus</th>
            <th>Amount</th>
            <th>Date</th>
            <th>Method</th>
            <th>OR Number</th>
            <th>Description</th>
            <th>Income Account</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {visiblePayments.map((pay, idx) => {
            const student = allStudents.find(s => s.id === pay.studentId);
            const account = accounts.find(a => a.id === pay.accountId);
            return (
              <tr key={idx}>
                <td>{pay.id || idx + 1}</td>
                <td>{pay.payer || (student ? (student.fullname || student.name) : "")}</td>
                <td>{normCampus(pay.campus || (student ? student.campus : "") || "")}</td>
                <td>₱{Number(pay.amount || 0).toLocaleString()}</td>
                <td>{pay.date}</td>
                <td>{pay.method}</td>
                <td>{pay.reference}</td>
                <td>{pay.description || ""}</td>
                <td>{account ? account.name : ""}</td>
                <td>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={async () => {
                      try {
                        if (!USE_LOCAL_FALLBACK) {
                          await deletePaymentTx(String(pay.id));
                          const rows = await fetchPaymentsSvc();
                          setPayments(rows);
                        } else {
                          const rows = await deletePaymentTx(String(pay.id));
                          setPayments(rows);
                        }
                      } catch (err) {
                        const newPayments = payments.filter((_, i) => i !== idx);
                        setPayments(newPayments);
                        try { localStorage.setItem("payments", JSON.stringify(newPayments)); } catch (e) { /* no-op */ }
                      }
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Payment Form Modal */}
      <Modal 
        isOpen={showForm} 
        onClose={() => setShowForm(false)} 
        title="Record Payment"
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" form="paymentForm" className="btn btn-warning">Save</button>
          </>
        }
      >
        <form id="paymentForm" onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Payer</label>
            <input type="text" className="form-control" name="payer" value={form.payer} onChange={handleChange} required />
          </div>
          <div className="mb-3">
            <label className="form-label">Amount</label>
            <input type="number" className="form-control" name="amount" value={form.amount} onChange={handleChange} required />
          </div>
          <div className="mb-3">
            <label className="form-label">Date</label>
            <input type="date" className="form-control" name="date" value={form.date} onChange={handleChange} required />
          </div>
          <div className="mb-3">
            <label className="form-label">Method</label>
            <select className="form-select" name="method" value={form.method} onChange={handleChange}>
              <option>Cash</option>
              <option>Check</option>
              <option>Bank Transfer</option>
              <option>Online</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">OR Number</label>
            <input type="text" className="form-control" name="reference" value={form.reference} onChange={handleChange} placeholder="Auto if blank" />
          </div>
          <div className="mb-3">
            <label className="form-label">Student</label>
            <select
              className="form-select"
              name="studentId"
              value={form.studentId}
              onChange={handleChange}
              required
            >
              <option value="">Select Student</option>
              {allStudents.map(student => (
                <option key={student.id} value={student.id}>{(student.fullname || student.name)} ({student.campus})</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Income Account</label>
            <select
              className="form-select"
              name="accountId"
              value={form.accountId}
              onChange={handleChange}
              required
            >
              <option value="">Select Account</option>
              {filteredIncomeAccounts.map(account => (
                <option key={account.id} value={account.id}>{account.name}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Collection Category</label>
            <select
              className="form-select"
              name="category"
              value={form.category}
              onChange={handleChange}
            >
              <option>Tuition</option>
              <option>Certificate</option>
              <option>Other</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Description (e.g., Certificate: Form 137)</label>
            <input type="text" className="form-control" name="description" value={form.description} onChange={handleChange} placeholder="Optional details" />
          </div>
        </form>
      </Modal>
    </div>
  );
}
